import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { DepartmentIntelligenceDetailResponse } from "@/api/intelligence";
import { formatCount, formatInr } from "@/lib/intelligenceFormat";
import { IntelligenceVariance } from "./IntelligenceVariance";
import { DoctorPerformanceChart } from "./DoctorPerformanceChart";

type Props = {
	detail: DepartmentIntelligenceDetailResponse;
};

export function DepartmentDetailPanel({ detail }: Props) {
	const c = detail.current;

	return (
		<div className="space-y-4">
			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-lg">{detail.department.departmentName}</CardTitle>
					<p className="text-sm text-muted-foreground">
						{c.doctorCount} doctors · {detail.period.label}
					</p>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<div>
							<p className="text-xs text-muted-foreground">Revenue</p>
							<p className="text-xl font-semibold tabular-nums">{formatInr(c.revenue)}</p>
							<IntelligenceVariance percent={c.revenueVariancePct} trend={c.trend} />
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Patients</p>
							<p className="text-xl font-semibold tabular-nums">{formatCount(c.patientVolume)}</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">OPD Visits</p>
							<p className="text-xl font-semibold tabular-nums">{formatCount(c.opdVisits)}</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Surgeries</p>
							<p className="text-xl font-semibold tabular-nums">{formatCount(c.surgeries)}</p>
						</div>
					</div>

					{detail.statements.length > 0 && (
						<ul className="mt-4 space-y-1 text-sm text-slate-700 border-t pt-4">
							{detail.statements.map((s) => (
								<li key={s}>• {s}</li>
							))}
						</ul>
					)}
				</CardContent>
			</Card>

			<DoctorPerformanceChart
				revenueTrend={detail.revenueTrend}
				title="Department revenue trend"
			/>

			<Card>
				<CardHeader className="pb-2">
					<CardTitle className="text-base font-medium">Service mix (revenue)</CardTitle>
				</CardHeader>
				<CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
					{(
						[
							["OPD", detail.serviceMix.opd],
							["Additional Services", detail.serviceMix.additionalServices ?? 0],
							["IPD", detail.serviceMix.ipd],
							["Lab", detail.serviceMix.lab],
							["Pharma", detail.serviceMix.pharma],
							["Day Care", detail.serviceMix.daycare],
						] as const
					).map(([label, val]) => (
						<div key={label} className="rounded-lg border p-3">
							<p className="text-muted-foreground text-xs">{label}</p>
							<p className="font-semibold tabular-nums">{formatInr(val)}</p>
						</div>
					))}
				</CardContent>
			</Card>

			{detail.doctorContributions.length > 0 && (
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-base font-medium">Doctor contributions</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						{detail.doctorContributions.map((d) => (
							<div
								key={d.doctorId}
								className="flex justify-between text-sm border-b pb-2 last:border-0 gap-4"
							>
								<span className="font-medium">{d.name}</span>
								<span className="text-muted-foreground tabular-nums shrink-0">
									{formatCount(d.patientVolume)} patients
								</span>
								<span className="font-medium tabular-nums shrink-0">
									{formatInr(d.revenue)}
									{d.revenueContributionPct != null && (
										<span className="text-muted-foreground font-normal ml-1">
											({d.revenueContributionPct.toFixed(1)}%)
										</span>
									)}
								</span>
							</div>
						))}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
